'use client';

import React, { ReactNode, useMemo, useState } from 'react';

import { useAuthentication, UserGroups } from '@ravago/shared/authentication';
import { RoleBasedToggle, UnitSystem } from '@ravago/shared/page-data/models/config';
import { BasicFacetWithTermsV2, ElasticPhysicalProperty, ElasticProduct } from '@ravago/shared/page-data/models/elastic';
import { RendererGlobals } from '@ravago/shared/page-data/models/utils';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Checkbox } from '@ravago/shared/radiance/components/checkbox/components/Checkbox/Checkbox';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { InputField } from '@ravago/shared/radiance/components/input-field/components/InputField/InputField';
import { List } from '@ravago/shared/radiance/components/list/components/List/List';
import { ListItem } from '@ravago/shared/radiance/components/list/components/ListItem/ListItem';
import { Menu } from '@ravago/shared/radiance/components/menu/components/Menu/Menu';
import { Table } from '@ravago/shared/radiance/components/table/components/Table/Table';
import { TableBody } from '@ravago/shared/radiance/components/table/components/TableBody/TableBody';
import { TableCell } from '@ravago/shared/radiance/components/table/components/TableCell/TableCell';
import { TableHead } from '@ravago/shared/radiance/components/table/components/TableHead/TableHead';
import { TableRow } from '@ravago/shared/radiance/components/table/components/TableRow/TableRow';
import { TableSubheader } from '@ravago/shared/radiance/components/table/components/TableSubheader/TableSubheader';
import { Tooltip } from '@ravago/shared/radiance/components/tooltip/components/Tooltip/Tooltip';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { firstBy } from 'thenby';

import { useProductCompare } from '../../../../../../hooks/use-product-compare';
import { PhysicalPropertiesUtils } from '../../../../../../utils/product/physical-properties.utils';
import { ProductCardBadge } from '../../../../../shared/cards/product-card/components/product-card-badge';
import { notchedImpactConfig, propertyUnitLabels } from '../../../../../shared/physical-property-display/mappers/property-unit-labels.mapper';
import { PhysicalProperty } from '../../../../../shared/physical-property-display/physical-property';
import { ConsumerWrapper } from '../../../../consumer-wrapper/consumer-wrapper';
import { DynamicPageLink } from '../../../../dynamic-page-link/dynamic-page-link';
import { MockLink } from '../../../../mock-link/mock-link';
import { TableInfo } from '../../../models/table-info.model';

interface Props {
   baseRoutes?: {
      product: string;
      technology: string;
   };
   activeFacets?: BasicFacetWithTermsV2[];
   products: Array<ElasticProduct>;
   hasPublicLabelAndSigningsPermission: boolean;
   hasSustainabilityLabels: boolean;
   allowShowPhyiscalProperties: boolean;
   globals: RendererGlobals;
   unitSystem: UnitSystem;
   activeColumnsUpdate: (newActiveColumns: string[]) => void;
   activeColumnKeys?: string[];
   roleBasedToggles: RoleBasedToggle;
}

export const ProductTable: React.FC<Props> = ({
   baseRoutes,
   products,
   activeFacets,
   hasPublicLabelAndSigningsPermission,
   allowShowPhyiscalProperties,
   hasSustainabilityLabels,
   globals,
   unitSystem,
   activeColumnKeys,
   activeColumnsUpdate,
   roleBasedToggles
}) => {
   const translate = useTranslations();
   const { user } = useAuthentication();
   const { hasProductCompareFeatureToggle } = roleBasedToggles;
   const { productItems, addProduct, removeProduct } = useProductCompare();
   const [optionOpen, setOptionOpen] = useState(false);
   const [searchValue, setSearchValue] = useState('');

   const listedProperties = (list: ReactNode) => (
      <Box direction="column" gap="3xs">
         {list}
      </Box>
   );

   const columns: TableInfo[] = useMemo(
      () => [
         {
            key: 'subgroup',
            isActive: true,
            width: '164px',
            title: translate('product.card.polymer-type'),
            value: (product: ElasticProduct) => product?.polymerSubGroup
         },
         {
            key: 'feature',
            isActive: true,
            width: '500px',
            title: translate('card-product-card-features'),
            value: (product: ElasticProduct) => (product?.features?.length ? product?.features.join(' • ') : '-')
         },
         {
            key: 'processing-method',
            isActive: true,
            width: '324px',
            title: translate('card-product-card-technologies'),
            value: (product: ElasticProduct) =>
               product?.processes?.length
                  ? product?.processes.map((technology, index) => (
                       <ConsumerWrapper
                          key={`${product.id}-${technology.slug}`}
                          isConsumer={globals.isConsumer}
                          renderConsumer={(children) => (
                             <DynamicPageLink
                                id={`table-body-technology-${technology.name}-link-${product.id}`}
                                locale={globals.locale}
                                slug={technology.slug}
                                baseRoute={{ url: baseRoutes?.technology, noFollow: false }}
                             >
                                {children}
                             </DynamicPageLink>
                          )}
                          renderProducer={(children) => <MockLink underline="none">{children}</MockLink>}
                       >
                          {technology.name}
                          {product.processes && index < product.processes.length - 1 && ', '}
                       </ConsumerWrapper>
                    ))
                  : '-'
         },
         {
            key: 'filler',
            isActive: true,
            width: '200px',
            title: translate('card-product-card-filler'),
            value: (product: ElasticProduct) =>
               product?.fillers?.length
                  ? product?.fillers.map((filler) => `${filler.percentage ?? '?'}% ${filler.name}`).join(', ')
                  : translate('card-product-card-unfilled')
         },
         ...(allowShowPhyiscalProperties
            ? [
                 {
                    key: 'density',
                    isActive: true,
                    width: '200px',
                    title: `${translate('product.information.physical-properties.density')}  ${propertyUnitLabels.density[unitSystem]}`,
                    value: (product: ElasticProduct) =>
                       product?.density?.length
                          ? listedProperties(
                               product.density.map((density) => (
                                  <PhysicalProperty
                                     key={`density-${density.value}-${density.testingMethod}-${product.slug}`}
                                     inheritSize
                                     value={density.value}
                                     condition={density.testingMethod}
                                  />
                               ))
                            )
                          : '-'
                 },
                 {
                    key: 'flame-rating',
                    isActive: true,
                    width: '200px',
                    title: translate('product.information.physical-properties.flame-rating'),
                    value: (product: ElasticProduct) =>
                       product?.flameRating?.length
                          ? listedProperties(
                               product.flameRating
                                  .filter((flameRating) => flameRating.unit === unitSystem)
                                  .sort(
                                     firstBy('value', 'desc').thenBy((v1: ElasticPhysicalProperty, v2: ElasticPhysicalProperty) =>
                                        PhysicalPropertiesUtils.getNumberFromCondition(unitSystem, v1.condition ?? null) >
                                        PhysicalPropertiesUtils.getNumberFromCondition(unitSystem, v2.condition ?? null)
                                           ? 1
                                           : -1
                                     )
                                  )
                                  .map((flameRating) => (
                                     <PhysicalProperty
                                        key={`flameRating-${flameRating.value}-${flameRating.condition}-${flameRating.testingMethod}-${product.slug}`}
                                        inheritSize
                                        value={flameRating.value}
                                        condition={`${flameRating.condition}, ${flameRating.testingMethod}`}
                                     />
                                  ))
                            )
                          : '-'
                 },
                 {
                    key: 'flow-rate',
                    isActive: true,
                    width: '250px',
                    title: `${translate('product.information.physical-properties.flow-rate')} ${propertyUnitLabels.flowRate[unitSystem]}`,
                    value: (product: ElasticProduct) =>
                       product?.flowRate?.length
                          ? listedProperties(
                               product.flowRate
                                  .sort(
                                     firstBy((v1: ElasticPhysicalProperty, v2: ElasticPhysicalProperty) =>
                                        (parseFloat(v1.value) ?? 0) > (parseFloat(v2.value) ?? 0) ? 1 : -1
                                     )
                                  )
                                  .map((flowRate) => (
                                     <PhysicalProperty
                                        key={`flowRate-${flowRate.value}-${flowRate.condition}-${flowRate.testingMethod}-${product.slug}`}
                                        inheritSize
                                        value={flowRate.value}
                                        condition={`${flowRate.condition}, ${flowRate.testingMethod}`}
                                     />
                                  ))
                            )
                          : '-'
                 },
                 {
                    key: 'notched-impact',
                    isActive: true,
                    width: '280px',
                    title: translate('products-overview-filters-notched-izod-impact-dynamic', notchedImpactConfig.ASTM[unitSystem]),
                    value: (product: ElasticProduct) => {
                       const astmImpact = product.notchedImpact?.filter((impact) => impact.testingMethod.includes('ASTM'));
                       return astmImpact?.length
                          ? listedProperties(
                               astmImpact
                                  .filter((impact) => impact.unit === unitSystem)
                                  .sort(
                                     firstBy((v1: ElasticPhysicalProperty, v2: ElasticPhysicalProperty) =>
                                        (parseFloat(v1.value) ?? 0) > (parseFloat(v2.value) ?? 0) ? 1 : -1
                                     )
                                  )
                                  .map((impact) => (
                                     <PhysicalProperty
                                        key={`impact-astm-${impact.value}-${impact.condition}-${impact.testingMethod}-${product.slug}`}
                                        inheritSize
                                        value={impact.value}
                                        condition={`${impact.condition}, ${impact.testingMethod}`}
                                     />
                                  ))
                            )
                          : '-';
                    }
                 },
                 {
                    key: 'notched-impact-strength',
                    isActive: true,
                    width: '280px',
                    title: translate('products-overview-filters-notched-izod-impact-dynamic', notchedImpactConfig.ISO[unitSystem]),
                    value: (product: ElasticProduct) => {
                       const isoImpact = product.notchedImpactStrength?.length
                          ? product.notchedImpactStrength
                          : product.notchedImpact?.filter((impact) => impact.testingMethod.includes('ISO'));
                       return isoImpact?.length
                          ? listedProperties(
                               isoImpact
                                  .filter((impact) => impact.unit === unitSystem)
                                  .sort(
                                     firstBy((v1: ElasticPhysicalProperty, v2: ElasticPhysicalProperty) =>
                                        (parseFloat(v1.value) ?? 0) > (parseFloat(v2.value) ?? 0) ? 1 : -1
                                     )
                                  )
                                  .map((impact) => (
                                     <PhysicalProperty
                                        key={`impact-${impact.value}-${impact.condition}-${impact.testingMethod}-${product.slug}`}
                                        inheritSize
                                        value={impact.value}
                                        condition={`${impact.condition}, ${impact.testingMethod}`}
                                     />
                                  ))
                            )
                          : '-';
                    }
                 }
              ]
            : [])
      ],
      [unitSystem]
   );

   const nonNullActiveColumnKeys = useMemo(() => activeColumnKeys ?? [...columns].map((column) => column.key), [activeColumnKeys, columns]);

   const activeColumns = useMemo(
      () => nonNullActiveColumnKeys.map((key) => columns.find((column) => column.key === key)).filter((column) => !!column) as TableInfo[],
      [nonNullActiveColumnKeys, columns]
   );

   const sortedColumns = useMemo(
      () =>
         [...activeColumns].sort(
            (a, b) =>
               (activeFacets ?? []).findIndex((facet) => facet.facetName === b.key) -
               (activeFacets ?? []).findIndex((facet) => facet.facetName === a.key)
         ),
      [activeFacets, activeColumns]
   );

   const searchFilteredColumns = useMemo(() => columns.filter((column) => column.key.includes(searchValue)), [searchValue]);

   const checked = (id: string) => productItems.some((item) => item.id === id);

   const handleProductCompare = (id: string, name: string) => {
      if (!id || !hasProductCompareFeatureToggle) return;

      const newChecked = !checked(id);

      if (newChecked) {
         addProduct(id, name);
      } else {
         removeProduct(id);
      }
   };

   const openOption = () => {
      setOptionOpen(!optionOpen);
   };

   const optionClick = (column: TableInfo) => {
      const index = nonNullActiveColumnKeys.findIndex((key) => key === column.key);
      let newActiveColumnKeys;
      if (index !== -1) {
         newActiveColumnKeys = nonNullActiveColumnKeys.filter((key) => key !== column.key);
      } else {
         newActiveColumnKeys = [...nonNullActiveColumnKeys, column.key];
      }
      activeColumnsUpdate(newActiveColumnKeys);
   };

   return (
      <Box position="relative" width="100%">
         <Box overflowX="scroll" width="100%">
            <Table
               background="default"
               rowDividers
               hover
               stickyOption={
                  <Menu
                     open={optionOpen}
                     onMenuClose={openOption}
                     closeOnOutsideClick
                     content={
                        <Box direction="column" gap="xs">
                           <Box direction="column" spacing={{ top: 'xs', left: 'xs', right: 'xs' }}>
                              <InputField
                                 id="table-options-column-search"
                                 data-testid="options-column-search"
                                 label="Search"
                                 trailingIcon="search"
                                 onChange={setSearchValue}
                                 value={searchValue}
                                 clearable
                              />
                           </Box>
                           <List>
                              {searchFilteredColumns.map((column) => (
                                 <div key={`menu-${column.key}`}>
                                    <ListItem
                                       data-testid={`options-listitem-${column.key}`}
                                       id={`table-options-column-${column.key}`}
                                       onClick={() => optionClick(column)}
                                    >
                                       <Box items="center" gap="2xs">
                                          <Checkbox
                                             readOnly
                                             checked={activeColumns.some((col) => col.key === column.key)}
                                             id={`table-options-column-checkbox-${column.key}`}
                                          />
                                          <Typography>{column.title}</Typography>
                                       </Box>
                                    </ListItem>
                                 </div>
                              ))}
                           </List>
                        </Box>
                     }
                  >
                     <IconButton icon="sliders" onClick={openOption} ariaLabel="Product Table Options" backdrop />
                  </Menu>
               }
            >
               <TableHead>
                  <TableRow>
                     {hasProductCompareFeatureToggle && (
                        <TableSubheader width="76px" sticky>
                           {undefined}
                        </TableSubheader>
                     )}
                     <TableSubheader sticky stickyOffset={hasProductCompareFeatureToggle ? '76px' : '0'}>
                        <Box id="tabel-header-product" height="44px" items="center">
                           <Typography variant="body-highlight">{translate('my-orders.table.product')}</Typography>
                        </Box>
                     </TableSubheader>
                     {sortedColumns.map((column) => (
                        <TableSubheader key={`column-header-${column.key}`}>
                           <Box id={`table-header-${column.key}`} data-testid={`table-header-${column.key}`} height="44px" items="center">
                              <Typography variant="body-highlight">{translate(column.title)}</Typography>
                           </Box>
                        </TableSubheader>
                     ))}
                  </TableRow>
               </TableHead>
               <TableBody>
                  {products.map((product) => (
                     <TableRow key={product.id}>
                        {hasProductCompareFeatureToggle && (
                           <TableCell width="76px" verticalAlign="top" sticky>
                              <Checkbox
                                 data-testid={`checkbox-${product.id}`}
                                 id={`table-body-checkbox-${product.id}`}
                                 disabled={productItems.length >= 5 && !checked(`product-${product.id}`)}
                                 checked={checked(`product-${product.id}`)}
                                 onChange={() => handleProductCompare(`product-${product.id}`, product.name)}
                              />
                           </TableCell>
                        )}
                        <TableCell width="283px" verticalAlign="top" sticky stickyOffset={hasProductCompareFeatureToggle ? '76px' : '0'}>
                           <Box direction="column" gap="2xs">
                              <Box gap="xs" items="center">
                                 <ConsumerWrapper
                                    isConsumer={globals.isConsumer}
                                    renderConsumer={(children) => (
                                       <DynamicPageLink
                                          id={`table-body-product-${product.id}`}
                                          baseRoute={{ url: baseRoutes?.product, noFollow: false }}
                                          slug={product.slug}
                                          locale={globals.locale}
                                       >
                                          {children}
                                       </DynamicPageLink>
                                    )}
                                    renderProducer={(children) => <MockLink>{children}</MockLink>}
                                 >
                                    <Typography color="inherit" variant="small">
                                       {product.name}
                                    </Typography>
                                 </ConsumerWrapper>
                                 {product.isPreferred &&
                                    hasPublicLabelAndSigningsPermission &&
                                    (user?.groups?.includes(UserGroups.ravagoSalesRepresentative) || !globals.isConsumer) && (
                                       <Tooltip text={translate('card-product-card-preferred')} pointers={false} align="end" direction="bottom">
                                          <Icon data-testid={`icon-${product.id}`} icon="gem" color="primary" />
                                       </Tooltip>
                                    )}
                              </Box>
                              {hasPublicLabelAndSigningsPermission && (
                                 <ProductCardBadge
                                    badgeInfo={
                                       {
                                          ...(product.sustainabilityGroup?.subSustainability?.icon && {
                                             sustainability: {
                                                icon: product.sustainabilityGroup.subSustainability.icon
                                             }
                                          }),
                                          quality: product.quality
                                       } as any
                                    }
                                    hasSustainabilityLabels={hasSustainabilityLabels}
                                 />
                              )}
                           </Box>
                        </TableCell>
                        {sortedColumns.map((column) => (
                           <TableCell width={column.width} key={`cell-${column.key}`} verticalAlign="top">
                              <Typography variant="small" data-testid={`text-${column.key}`}>
                                 {column.value(product)}
                              </Typography>
                           </TableCell>
                        ))}
                     </TableRow>
                  ))}
               </TableBody>
            </Table>
         </Box>
      </Box>
   );
};
